import React, { useState, useEffect } from 'react';
import { api } from './constants';
import { UserPlus, Trash2, Shield, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPanel({ me }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'team_member' });
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const res = await api.get('/users');
    if (res.ok) setUsers(await res.json());
    else if (res.status === 403) toast.error('Admin access required');
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.password) { toast.error('Email and password required'); return; }
    const res = await api.post('/users', form);
    if (res.ok) { toast.success('User created'); setForm({ name: '', email: '', password: '', role: 'team_member' }); setShowForm(false); load(); }
    else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
  };

  const toggleActive = async (u) => {
    const res = await api.patch(`/users/${u.id}`, { active: !u.active });
    if (res.ok) { toast.success('Updated'); load(); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    const res = await api.del(`/users/${u.id}`);
    if (res.ok) { toast.success('Deleted'); load(); }
    else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
  };

  const inputCls = 'bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none';

  return (
    <div data-testid="users-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Admin Users</h2>
        <button onClick={() => setShowForm(s => !s)} data-testid="add-user-btn" className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2 rounded"><UserPlus className="w-4 h-4" />Add User</button>
      </div>

      {showForm && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="user-form">
          <input className={inputCls} placeholder="Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          <input className={inputCls} placeholder="Email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="user-email-input" />
          <input className={inputCls} type="password" placeholder="Password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} data-testid="user-password-input" />
          <select className={inputCls} value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
            <option value="team_member">Team Member</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={create} data-testid="create-user-btn" className="bg-green-600 hover:bg-green-700 text-white text-sm rounded px-4 py-2">Create</button>
        </div>
      )}

      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400 text-xs">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t border-slate-700">
                <td className="px-4 py-3 text-white">{u.name}</td>
                <td className="px-4 py-3 text-slate-300">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 w-fit ${u.role === 'admin' ? 'bg-orange-500/20 text-orange-300 border-orange-500/40' : 'bg-slate-600/30 text-slate-300 border-slate-600'}`}>
                    {u.role === 'admin' ? <Shield className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}{u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(u)} className={`text-xs px-2 py-0.5 rounded-full ${u.active !== false ? 'bg-green-500/20 text-green-300' : 'bg-slate-600/30 text-slate-400'}`}>
                    {u.active !== false ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.email !== me?.email && <button onClick={() => remove(u)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
